/**
 * Frame.io-style frame comments scoped to a project's video.
 *
 * Comments are anchored at (timecode_sec, x_pct, y_pct) and threaded — top
 * level comments may have replies. The overlay component reads + posts here.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export interface FrameComment {
  comment_id: string
  /** Author display name. */
  author_name: string
  /** Author role for color-coding pins (producer = amber, others = blue). */
  author_role: 'actor' | 'supervisor' | 'producer' | 'member' | 'owner'
  /** Anchor on the video. */
  timecode_sec: number
  /** Position as a percentage of the frame; 0..1. */
  x_pct: number
  y_pct: number
  body: string
  created_at: string
  /** Has the current user resolved this comment thread? */
  resolved_for_me: boolean
  /** Number in the visible pin overlay (1-based, oldest first). */
  pin_number: number
  /** Threaded replies. */
  replies: FrameCommentReply[]
}

export interface FrameCommentReply {
  reply_id: string
  author_name: string
  author_role: FrameComment['author_role']
  body: string
  created_at: string
}

const projectCommentsKey = (projectId: string) => ['project', projectId, 'frame-comments'] as const

/** All frame comments on this project, paginated server-side. */
export function useProjectComments(projectId: string) {
  return useQuery({
    queryKey: projectCommentsKey(projectId),
    queryFn: () => api<{ comments: FrameComment[] }>(`/api/v1/projects/${projectId}/comments`),
    enabled: Boolean(projectId),
  })
}

/** Post a new top-level frame comment. */
export function usePostFrameComment(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      timecodeSec: number
      xPct: number
      yPct: number
      body: string
      /** When present, comment anchors to the cue (target_type='cue') so it
       *  shows up in cue-scoped lists in addition to the project overlay. */
      cueId?: string
    }) =>
      api<{ comment: FrameComment }>(`/api/v1/projects/${projectId}/comments`, {
        method: 'POST',
        body: JSON.stringify({
          timecode_sec: input.timecodeSec,
          x_pct: input.xPct,
          y_pct: input.yPct,
          body: input.body,
          ...(input.cueId ? { cue_id: input.cueId, target_type: 'cue' } : {}),
        }),
      }),
    onSuccess: (res) => {
      // Splice the new comment into the cache immediately so the pin renders
      // before the network refetch finishes. e2e tests (david agent) check
      // for `[data-frame-pin]` right after mutateAsync resolves and would
      // otherwise race the refetch.
      qc.setQueryData<{ comments: FrameComment[] } | undefined>(
        projectCommentsKey(projectId),
        (prev) => {
          if (!prev) return { comments: [res.comment] }
          // Skip if the server-side echo somehow already arrived via refetch.
          if (prev.comments.some((c) => c.comment_id === res.comment.comment_id)) return prev
          return { comments: [...prev.comments, res.comment] }
        },
      )
      // Still invalidate so eventual list (counts, pin numbers, replies)
      // re-syncs with the server's canonical ordering.
      void qc.invalidateQueries({ queryKey: projectCommentsKey(projectId) })
    },
  })
}

/** Post a reply to an existing comment thread. */
export function useReplyFrameComment(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { commentId: string; body: string }) =>
      api<{ reply: FrameCommentReply }>(
        `/api/v1/projects/${projectId}/comments/${input.commentId}/replies`,
        {
          method: 'POST',
          body: JSON.stringify({ body: input.body }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectCommentsKey(projectId) })
    },
  })
}

/** Mark a comment thread as resolved-for-me (does not affect other users). */
export function useResolveFrameComment(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { commentId: string }) =>
      api<{ ok: true }>(`/api/v1/projects/${projectId}/comments/${input.commentId}/resolve`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectCommentsKey(projectId) })
    },
  })
}
